import { html } from 'lit-element';
import { ifNotNull } from '../../utils/directives';
import {
  KatalComponent,
  KatLitElement,
  booleanConverter,
} from '../../shared/base';

/**
 * @component {kat-button} KatalButton Buttons provide the ability for our customers to trigger an action.
 * @status Production
 * @theme flo
 * @event click Fired when the button is clicked.
 * @example Primary {"label": "Primary Button", "variant":"primary"}
 * @example Secondary {"label": "Secondary Button", "variant":"secondary"}
 * @example Tertiary {"label": "Tertiary Button", "variant": "tertiary"}
 * @example Link {"label": "Button as link", "variant":"link"}
 * @example Warning {"label": "Warning Button", "variant":"warning"}
 * @example Danger {"label": "Danger Button", "variant":"danger"}
 * @guideline Do Buttons are reserved for actions.
 * @guideline Do Use the primary button state for the most common or primary actions a customer will take on the page.
 * @guideline Do Use sentence case (not title case).
 * @guideline Do Labels should have a maximum of two words (three on rare occasions).
 * @guideline Dont Do not use more than one primary button per page.
 * @guideline Do Use 'responsive' attribute to have the button shrink on smaller screens
 * @guideline Do Use tertiary buttons when more contrast is needed than can be provided by primary and secondary buttons.
 */
export class KatalButton extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {string} label This is the text of the button that will be displayed to the user
       * @required
       */
      label: { type: String, reflect: true },

      /**
       * @classprop {string} value This is the value of the button
       */
      value: { type: String, reflect: true },

      /**
       * @classprop {string} width This will set the width of the button
       */
      width: { type: String, reflect: true },

      /**
       * @classprop {enum} form-action The action a button performs. Defaults to "button".
       * @enum {value} button Clickable button
       * @enum {value} submit Clickable button that submits form data
       * @enum {value} reset Clickable button the clears a form
       */
      formAction: { type: String, attribute: 'form-action', reflect: true },

      /**
       * @classprop {boolean} disabled If present, the button is not clickable. Does not work on the link variant.
       */
      disabled: { type: Boolean, converter: booleanConverter, reflect: true },

      /**
       * @classprop {boolean} responsive If present, the button will adjust its appearance on smaller screens.
       */
      responsive: { type: Boolean, converter: booleanConverter, reflect: true },

      /**
       * @classprop {enum} variant Specifies the type of button to be displayed. Defaults to "primary".
       * @enum {value} primary This is the default button type.
       * @enum {value} secondary Button type for a secondary action.
       * @enum {value} tertiary A lighter alternative button style.
       * @enum {value} link Looks like a hyperlink but emits the same events as other kat-buttons
       * @enum {value} warning Button type indicating warning states/situations
       * @enum {value} danger Button type indicating danger states/situations
       */
      variant: { type: String, reflect: true },

      /**
       * @classprop {enum} size Specifies the visual size of the button. Defaults to "base".
       * @enum {value} base This is the default button size.
       * @enum {value} small This is a smaller button
       */
      size: { type: String, reflect: true },

      /**
       * @classprop {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
       * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
       * more details.
       */
      katal12SpacingFixes: {
        type: Boolean,
        converter: booleanConverter,
        attribute: 'katal-1-2-spacing-fixes',
        reflect: true,
      },
    };
  }

  constructor() {
    super();

    this.formAction = 'button';
    this.variant = 'primary';
    this.size = 'base';
  }

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  firstUpdated() {
    // prevent clicks from happening when the button is disabled
    this.addEventListener('click', e => {
      if (this.disabled) {
        e.stopImmediatePropagation();
      }
    });
  }

  render() {
    this.style.width = this.width || null;

    return html`
      <button
        class=${this.variant}
        type=${ifNotNull(this.formAction)}
        value=${ifNotNull(this.value)}
        ?disabled=${this.disabled}
      >
        ${this.label}
      </button>
    `;
  }
}

KatalComponent.register('kat-button', KatalButton);
